import React, { useState, useRef } from "react";
import { api, PLATFORM_LABELS, fmtEuro, fmtNum } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Upload, FileSpreadsheet, Loader2, CheckCircle2, AlertTriangle, ArrowRight, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

const TRIP_FIELD_LABELS = {
  date: "Date *",
  start_time: "Heure début",
  end_time: "Heure fin",
  pickup: "Départ",
  dropoff: "Arrivée",
  distance_km: "Distance (km)",
  duration_min: "Durée (min)",
  gross_amount: "Montant brut *",
  commission: "Commission",
  tolls: "Péages",
  tips: "Pourboire",
  notes: "Notes",
};

const REQUIRED_FIELDS = ["date", "gross_amount"];

export default function ImportCSV() {
  const fileRef = useRef(null);
  const [platform, setPlatform] = useState("uber");
  const [csvContent, setCsvContent] = useState("");
  const [fileName, setFileName] = useState("");
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [step, setStep] = useState(1); // 1=upload, 2=map, 3=result
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleFile = (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Fichier trop lourd (max 5 Mo)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setCsvContent(reader.result);
      setFileName(file.name);
    };
    reader.readAsText(file, "UTF-8");
  };

  const doPreview = async () => {
    if (!csvContent) {
      toast.error("Chargez un fichier CSV d'abord");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/imports/preview", { platform, csv_content: csvContent });
      setPreview(data);
      setMapping(data.suggested_mapping || {});
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible d'analyser le fichier");
    } finally {
      setLoading(false);
    }
  };

  const doCommit = async () => {
    for (const req of REQUIRED_FIELDS) {
      if (!mapping[req]) {
        toast.error(`Le champ « ${TRIP_FIELD_LABELS[req]} » doit être associé à une colonne`);
        return;
      }
    }
    setLoading(true);
    try {
      const { data } = await api.post("/imports/commit", {
        platform,
        csv_content: csvContent,
        mapping,
        skip_duplicates: skipDuplicates,
      });
      setResult(data);
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'import");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setCsvContent("");
    setFileName("");
    setPreview(null);
    setMapping({});
    setResult(null);
    setStep(1);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="overline mb-2">Automatisation</div>
        <h1 className="font-manrope text-3xl md:text-4xl font-extrabold">Import CSV — Uber / Bolt / Heetch</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Importez vos relevés hebdomadaires en un clic. Les colonnes sont détectées automatiquement — vous pouvez les ajuster.
        </p>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-3">
        <Step n={1} label="Fichier" active={step >= 1} done={step > 1} />
        <ArrowRight className="h-4 w-4 text-muted-foreground" />
        <Step n={2} label="Cartographie" active={step >= 2} done={step > 2} />
        <ArrowRight className="h-4 w-4 text-muted-foreground" />
        <Step n={3} label="Résultat" active={step >= 3} done={false} />
      </div>

      {/* STEP 1 — Upload */}
      {step === 1 && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="card-surface p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Plateforme</Label>
              <Select value={platform} onValueChange={setPlatform}>
                <SelectTrigger data-testid="select-import-platform"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["uber", "bolt", "heetch"].map((p) => (
                    <SelectItem key={p} value={p}>{PLATFORM_LABELS[p]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 space-y-1.5">
              <Label className="text-xs">Fichier CSV</Label>
              <label className="flex items-center gap-3 border-2 border-dashed border-border rounded-lg p-4 cursor-pointer hover:border-primary/40 transition-colors">
                <Upload className="h-5 w-5 text-primary" />
                <div className="flex-1 text-sm">
                  {fileName ? (
                    <span className="text-foreground font-medium">{fileName}</span>
                  ) : (
                    <>
                      <span className="text-foreground font-medium">Cliquez pour choisir</span>{" "}
                      <span className="text-muted-foreground">ou glissez-déposez un CSV (max 5 Mo)</span>
                    </>
                  )}
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  className="hidden"
                  data-testid="input-csv-file"
                />
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            {csvContent && (
              <Button variant="ghost" onClick={reset} data-testid="btn-import-reset">Réinitialiser</Button>
            )}
            <Button onClick={doPreview} disabled={!csvContent || loading} data-testid="btn-import-analyze">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />}
              Analyser
            </Button>
          </div>
          <div className="text-xs text-muted-foreground border-t border-border/60 pt-3 mt-2">
            <p className="mb-1"><strong className="text-foreground">Formats acceptés :</strong> CSV virgule ou point-virgule, UTF-8. Les dates au format ISO (2026-02-15) ou français (15/02/2026) sont reconnues.</p>
            <p>Un contrôle anti-doublon est appliqué (même date + montant + plateforme).</p>
          </div>
        </motion.div>
      )}

      {/* STEP 2 — Mapping */}
      {step === 2 && preview && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          <div className="card-surface p-4 flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm">
              <span className="text-muted-foreground">{preview.total_rows} ligne(s) détectée(s) — plateforme :</span>{" "}
              <span className="font-semibold text-primary">{PLATFORM_LABELS[preview.platform]}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Switch checked={skipDuplicates} onCheckedChange={setSkipDuplicates} data-testid="switch-skip-duplicates" />
              <span>Ignorer les doublons (même date + montant)</span>
            </div>
          </div>

          <div className="card-surface p-5">
            <h3 className="font-manrope font-semibold mb-3">Cartographie des colonnes</h3>
            <p className="text-xs text-muted-foreground mb-4">
              Associez chaque champ VTC à une colonne du CSV. Les champs marqués <span className="text-primary">*</span> sont obligatoires.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {preview.trip_fields.map((f) => (
                <div key={f} className="space-y-1.5">
                  <Label className={`text-xs ${REQUIRED_FIELDS.includes(f) ? "text-primary" : ""}`}>
                    {TRIP_FIELD_LABELS[f]}
                  </Label>
                  <Select
                    value={mapping[f] || "__none__"}
                    onValueChange={(v) => setMapping({ ...mapping, [f]: v === "__none__" ? null : v })}
                  >
                    <SelectTrigger data-testid={`select-map-${f}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Ignorer —</SelectItem>
                      {preview.columns.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>

          <div className="card-surface p-5">
            <h3 className="font-manrope font-semibold mb-3">Aperçu (5 premières lignes)</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-muted-foreground border-b border-border">
                  <tr>
                    {preview.columns.map((c) => (
                      <th key={c} className="text-left px-2 py-2 whitespace-nowrap">{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.sample.slice(0, 5).map((row, i) => (
                    <tr key={i} className="border-b border-border/60">
                      {preview.columns.map((c) => (
                        <td key={c} className="px-2 py-2 whitespace-nowrap">{row[c]}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-between gap-2">
            <Button variant="ghost" onClick={() => setStep(1)} data-testid="btn-import-back">Retour</Button>
            <Button onClick={doCommit} disabled={loading} data-testid="btn-import-commit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              Importer {preview.total_rows} ligne(s)
            </Button>
          </div>
        </motion.div>
      )}

      {/* STEP 3 — Result */}
      {step === 3 && result && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <ResultCard label="Importées" value={result.imported} icon={CheckCircle2} tone="success" testid="stat-imported" />
            <ResultCard label="Ignorées (doublons)" value={result.skipped} icon={Trash2} tone="muted" testid="stat-skipped" />
            <ResultCard label="Erreurs" value={result.total_errors} icon={AlertTriangle} tone={result.total_errors > 0 ? "destructive" : "muted"} testid="stat-errors" />
          </div>

          {result.errors && result.errors.length > 0 && (
            <div className="card-surface p-5">
              <h3 className="font-manrope font-semibold mb-3">Détails des erreurs</h3>
              <div className="max-h-64 overflow-y-auto space-y-1">
                {result.errors.map((e, i) => (
                  <div key={i} className="text-xs rounded-md bg-destructive/10 border border-destructive/25 text-destructive px-3 py-1.5">
                    Ligne {e.row} : {e.error}
                  </div>
                ))}
                {result.total_errors > result.errors.length && (
                  <div className="text-xs text-muted-foreground pt-2">
                    … et {result.total_errors - result.errors.length} autres erreurs.
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={reset} data-testid="btn-import-new">Nouvel import</Button>
            <Button onClick={() => (window.location.href = "/courses")} data-testid="btn-import-view-trips">
              Voir les courses <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </motion.div>
      )}
    </div>
  );
}

function Step({ n, label, active, done }) {
  return (
    <div className={`flex items-center gap-2 ${active ? "text-foreground" : "text-muted-foreground"}`}>
      <span
        className={`inline-flex items-center justify-center h-6 w-6 rounded-full text-xs font-bold ${
          done ? "bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))]"
            : active ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
        }`}
      >
        {done ? "✓" : n}
      </span>
      <span className="text-sm font-medium">{label}</span>
    </div>
  );
}

function ResultCard({ label, value, icon: Icon, tone, testid }) {
  const tones = {
    success: "border-[hsl(var(--success))]/30 bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]",
    destructive: "border-destructive/30 bg-destructive/10 text-destructive",
    muted: "border-border bg-card text-muted-foreground",
  };
  return (
    <div className={`rounded-xl border p-4 ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="overline">{label}</span>
        <Icon className="h-4 w-4" />
      </div>
      <div className="font-manrope font-extrabold text-3xl num text-foreground">{fmtNum(value)}</div>
    </div>
  );
}
